import { Box, Button, HStack, Input, VStack, useColorModeValue, useToast,Text,
  Flex,
  Image,
  Spinner,} from "@chakra-ui/react";
import { useState } from "react";
import axios from "axios";
import { Navigate, useNavigate, useLocation } from "react-router-dom";  // Import the useNavigate hook
import ReCAPTCHA from 'react-google-recaptcha';
import { useTenant } from "../Routers/TenantContextProvider";
import BackGroundImage from "../../resources/PlatformBackround.png"

interface LoginProps {
  onLogin: (newToken: string, refreshToken :string) => void; // Add this prop
}

interface LoginRequest {
    username: string;
    password: string;
    tenant_id: number;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {

      const overlayColor = useColorModeValue("rgba(255, 255, 255, 0.85)", "rgba(0, 0, 0, 0.65)");
      const textColor = useColorModeValue("gray.800", "whiteAlpha.900");
      const ctaColorScheme = useColorModeValue("blue", "cyan");

      const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
      const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

      // Access tenant context
      const { tenant, loading, error } = useTenant();

      const handleRecaptchaChange = (token: string | null) => {
        setRecaptchaToken(token);
        setRecaptchaError(null); // Clear error if recaptcha passes
      };

      const handleRecaptchaError = () => {
        setRecaptchaError('Failed to verify reCAPTCHA. Please try again.');
      };

      const toast = useToast();

      const recaptchaKey = process.env.REACT_APP_GOOGLE_CAPTCHA_KEY;
    
      if (!recaptchaKey) {
        throw new Error('REACT_APP_GOOGLE_CAPTCHA_KEY is not defined');
      }

        const [loginData, setLoginData] = useState<LoginRequest>({
            username: '',
            password: '',
            tenant_id: tenant?.tenant_id ?? 0  
        });

        const navigate = useNavigate();  // Use the hook here

        const location = useLocation();
        const params = new URLSearchParams(location.search);


          if (loading) {
          return (
            <Flex justify="center" align="center" h="100vh">
              <Spinner size="xl" />
            </Flex>
          );
        }
      
        if (error || !tenant) {
          return (
            <Flex justify="center" align="center" h="100vh">
              <Text color="red.500" fontSize="lg">
                Failed to load tenant details.
              </Text>
            </Flex>
          );
        }

        // Handle input changes
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            setLoginData({
                ...loginData,
                [name]: name === "tenant_id" ? parseInt(value) : value
            });
        };

        const handleLogin = async () => {
                console.log("Login button clicked from Login.tsx"); 

                if (!recaptchaToken) {
                  alert('Please complete the reCAPTCHA verification.');
                  setRecaptchaError('Please complete the reCAPTCHA verification.');
                    return;
                  }      

                  const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";

                  // Send the CAPTCHA token to your backend for verification
                  const response = await fetch(apiurl+"/verify-captcha", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ recaptchaToken }),
                });

                const data = await response.json();

                if (data.success) {
                  // alert("CAPTCHA verified successfully!");
                  // Proceed with form submission logic
                } else {
                  toast({
                    title: 'Captcha Failed',
                    description:
                      'Captcha Verification Failed. Please try again.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                  });
                  return;
                }

                try {
                    const loginapiurl = process.env.REACT_APP_LOGIN_API_URL || 'https://default-url.com';
                    if (!loginapiurl) {
                      throw new Error('REACT_APP_LOGIN_API_URL is not defined');
                  } 
                        console.log("Login Request"+JSON.stringify(loginData))
                        const response = await axios.post(loginapiurl, loginData);
                        const token = response.data.access_token;
                        const refreshToken = response.data.refresh_token;
                        
                        // Call onLogin with the received token
                        onLogin(token, refreshToken);
                        
                        // After successful login, redirect to dashboard
                        const tenantSlug = localStorage.getItem("tenantSlug");
                        // After successful login, redirect to dashboard
                        navigate(`/platform/${tenantSlug}/app/`, { replace: true });

                        // Trigger a full re-render on the same route to reflect new state
                        navigate(0);  // This forces React to reload components and pick up updated props
                      } catch (error) {
                        if (axios.isAxiosError(error) && error.response) {
                          const status = error.response.status;
                    
                          if (status === 429) {
                            toast({
                              title: 'Too Many Requests',
                              description:
                                'You have exceeded the rate limit. Please try again later.',
                              status: 'warning',
                              duration: 5000,
                              isClosable: true,
                            });
                          } else {
                            toast({
                              title: 'Login Failed',
                              description: error.response.data.detail ||
                                'An unexpected error occurred. Please try again.',
                              status: 'error',
                              duration: 5000,
                              isClosable: true,
                            });
                          }
                        } else {
                          toast({
                            title: 'Network Error',
                            description: 'Unable to connect to the server. Please check your network.',
                            status: 'error',
                            duration: 5000,
                            isClosable: true,
                          });
                        }
                      }
                    };
  return (

        <Box
      bgImage={`url(${BackGroundImage})`}
      bgSize="cover"
      bgPosition="center"
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      pt="72px"
    >
      {/* Top Section */}
      <Box
        bg={overlayColor}
        borderRadius="lg"
        boxShadow="lg"
        mx="auto"
        my={8}
        maxW="800px"
        textAlign="center"
        px={6}
        py={10}
      >
        {/* Logo */}
      <Flex justify="center" mb={6}>
        <Image
          src={tenant.logo_url}
          alt={`${tenant.tenant_name} logo`}
          boxSize={{ base: "160px", md: "200px" }}
          objectFit="cover"
          borderRadius="full"  // ⬅️ makes it fully rounded (circular)
        />
      </Flex>

        {/* Tenant Name */}
        <Text
          fontSize={{ base: "2xl", md: "4xl" }}
          fontWeight="bold"
          color={textColor}
          mb={2}
        >
         {tenant.tenant_name}
        </Text>

      <VStack spacing={4}>
        <Input 
          placeholder="Username"
          name="username"
          value={loginData.username}
          onChange={handleChange}
          required
        />
        <Input 
          placeholder="Password"
          type="password"
          name="password"
          value={loginData.password}
          onChange={handleChange}
         required
        />
        </VStack>

        <VStack mt={5} spacing={4}>
        {recaptchaError && <Text color="red.500">{recaptchaError}</Text>}
            {/* Recaptcha */}
            <ReCAPTCHA sitekey={recaptchaKey} onChange={handleRecaptchaChange} onErrored={handleRecaptchaError} />

        <Button   border="1px solid"
        borderColor="blue.200"
        borderRadius="md" width={40} onClick={handleLogin}>Login</Button>
        </VStack>
      </Box>

    {/* Footer Note */}
    <Box position="relative" bottom={0} width="100%" textAlign="center" px={4} pb={4}>
      <Text fontSize="md" color="gray.600">
        Eduvic Platform – An integrated platform by Eduvic Technologies combining Eduvic Assess and LMS.
      </Text>
    </Box>
  

      </Box>

  );
};

export default Login;
